/**
 * Find multiple distinct schedule slots via /api/schedule/preview (with avoidTimes).
 * Shared by package retry and edit-booking slot picker.
 */

export const AFTERNOON_START_MINUTES = 14 * 60;

export const parseTimeToMinutes = (time) => {
  if (time == null) return null;
  if (typeof time === 'number' && !Number.isNaN(time)) return time;
  if (typeof time === 'string') {
    const [hours, mins] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(mins)) return null;
    return hours * 60 + mins;
  }
  return null;
};

export const formatSlotTime = (time) => {
  if (time == null || time === undefined) return '—';
  if (typeof time === 'string') return time;
  if (typeof time === 'number' && !Number.isNaN(time)) {
    const hours = Math.floor(time / 60);
    const mins = time % 60;
    return `${hours}:${String(mins).padStart(2, '0')}`;
  }
  return '—';
};

export const classifySlotPeriod = (startTime) => {
  const minutes = parseTimeToMinutes(startTime);
  if (minutes == null) return 'morning';
  return minutes >= AFTERNOON_START_MINUTES ? 'afternoon' : 'morning';
};

export const slotDedupeKey = (slot) => {
  const therapists = formatSlotTherapists(slot).join('+');
  return `${parseTimeToMinutes(slot.startTime)}-${therapists}-${slot.room}`;
};

export const formatSlotTherapists = (slot) => {
  if (Array.isArray(slot?.therapists) && slot.therapists.length > 0) {
    return slot.therapists.map((t) => {
      const name = t.name || t;
      return t.is_primary ? `${name} (primary)` : name;
    });
  }
  if (slot?.therapist) return [slot.therapist];
  return [];
};

export const groupSlotsByPeriod = (slots) => {
  const morning = [];
  const afternoon = [];
  (slots || []).forEach((slot) => {
    if (slot.period === 'afternoon') afternoon.push(slot);
    else morning.push(slot);
  });
  const byTime = (a, b) => (a.startMinutes ?? 0) - (b.startMinutes ?? 0);
  return {
    morning: morning.sort(byTime),
    afternoon: afternoon.sort(byTime),
  };
};

const isConsultationTreatmentName = (treatmentName, availableTreatments = []) => {
  const meta = availableTreatments.find((t) => t.name === treatmentName);
  const typeUpper = (meta?.type || '').toUpperCase();
  const nameUpper = (treatmentName || '').toUpperCase();
  return typeUpper.includes('CONSULTATION') || nameUpper.includes('CONSULTATION');
};

/**
 * @param {object} options
 * @param {function} options.apiPost - apiPost from utils/api
 * @returns {Promise<{ slots: object[], failureHints: string[] }>}
 */
export async function findAvailableScheduleSlots({
  apiPost,
  treatment,
  date,
  guestName,
  guestId,
  guestGender = '',
  guestRoom = '',
  therapistGenderPreference = null,
  genderPreference = null,
  roomPreference = null,
  therapistPreference = null,
  assignedDoctor = null,
  currentBookingId = null,
  duration = null,
  customDuration = false,
  availableTreatments = [],
  allRoomsData = {},
  maxSlotsPerPeriod = 10,
  maxSlotsTotal = 20,
}) {
  const isConsultationType = isConsultationTreatmentName(treatment, availableTreatments);

  const relaxedStrategies = [
    { name: 'Flexible time', timeOfDay: null, anyRoom: false, anyTherapist: false },
    { name: 'Any room', timeOfDay: null, anyRoom: true, anyTherapist: false },
    { name: 'Any therapist', timeOfDay: null, anyRoom: false, anyTherapist: true },
    { name: 'Fully relaxed', timeOfDay: null, anyRoom: true, anyTherapist: true },
  ];

  const foundSlots = [];
  const seenSlotKeys = new Set();
  const avoidTimes = [];
  const failureHints = [];

  const recordAvoidTime = (slot) => {
    const startMin = parseTimeToMinutes(slot.startTime);
    if (startMin != null && !avoidTimes.includes(startMin)) {
      avoidTimes.push(startMin);
    }
  };

  const tryPreview = async (strategy) => {
    const preferTherapist =
      isConsultationType && !strategy.anyTherapist
        ? assignedDoctor || therapistPreference || null
        : strategy.anyTherapist
          ? null
          : therapistPreference || null;

    const requestData = {
      treatment,
      date,
      guestName,
      guestId,
      guestGender,
      guestRoom: guestRoom != null && guestRoom !== '' ? String(guestRoom) : '',
      preferredTimeOfDay: strategy.timeOfDay,
      therapistPreference: preferTherapist,
      roomPreference: strategy.anyRoom ? null : roomPreference || null,
      genderPreference: therapistGenderPreference || genderPreference || null,
      avoidTimes: [...avoidTimes],
      currentBookingId,
      duration,
      customDuration,
    };

    const response = await apiPost('/api/schedule/preview', requestData);

    if (!response.ok) {
      let detail = `${response.status} ${response.statusText}`;
      try {
        const errBody = await response.json();
        if (errBody?.detail) {
          detail = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
        }
      } catch {
        /* ignore */
      }
      failureHints.push(`${strategy.name}: ${detail}`);
      return null;
    }

    const slot = await response.json();

    if (slot?.error) {
      const msg = typeof slot.error === 'string' ? slot.error : JSON.stringify(slot.error);
      const typeSuffix = slot.error_type ? ` [${slot.error_type}]` : '';
      failureHints.push(`${strategy.name}:${typeSuffix} ${msg}`);
      return null;
    }

    if (!slot) return null;

    const roomData = allRoomsData[slot.room];
    const isInRoomTherapy = roomData?.location?.startsWith('Room No:');
    const guestRoomLocation = guestRoom ? `Room No:${guestRoom}` : null;

    if (isInRoomTherapy && roomData.location !== guestRoomLocation) {
      failureHints.push(`${strategy.name}: Skipped in-room therapy (${slot.room})`);
      recordAvoidTime(slot);
      return null;
    }

    return slot;
  };

  const addSlot = (slot, strategyName) => {
    const key = slotDedupeKey(slot);
    recordAvoidTime(slot);
    if (seenSlotKeys.has(key)) return false;
    seenSlotKeys.add(key);
    const startMinutes = parseTimeToMinutes(slot.startTime) ?? 0;
    foundSlots.push({
      ...slot,
      strategy: strategyName,
      period: classifySlotPeriod(slot.startTime),
      startMinutes,
      startTimeFormatted: formatSlotTime(slot.startTime),
      endTimeFormatted: formatSlotTime(slot.endTime),
      therapistLabels: formatSlotTherapists(slot),
    });
    return true;
  };

  const exhaustStrategy = async (strategy, maxForStrategy) => {
    let stagnantAttempts = 0;
    let addedForStrategy = 0;
    while (addedForStrategy < maxForStrategy && foundSlots.length < maxSlotsTotal) {
      const slot = await tryPreview(strategy);
      if (!slot) break;

      const added = addSlot(slot, strategy.name);
      if (!added) {
        stagnantAttempts += 1;
        if (stagnantAttempts >= 3) break;
      } else {
        stagnantAttempts = 0;
        addedForStrategy += 1;
      }
    }
  };

  await exhaustStrategy(
    { name: 'Morning', timeOfDay: 'morning', anyRoom: false, anyTherapist: false },
    maxSlotsPerPeriod,
  );
  await exhaustStrategy(
    { name: 'Afternoon', timeOfDay: 'afternoon', anyRoom: false, anyTherapist: false },
    maxSlotsPerPeriod,
  );
  if (isConsultationType) {
    await exhaustStrategy(
      { name: 'Afternoon (any doctor)', timeOfDay: 'afternoon', anyRoom: false, anyTherapist: true },
      maxSlotsPerPeriod,
    );
  }

  for (const strategy of relaxedStrategies) {
    if (foundSlots.length >= maxSlotsTotal) break;
    await exhaustStrategy(strategy, 4);
  }

  return {
    slots: foundSlots,
    failureHints: [...new Set(failureHints)].slice(0, 8),
  };
}
